/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { clone } from 'underscore';
import Backbone from 'backbone';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import BiddingStrategyView from '@bingads-webui-campaign-legacy/bidding-strategy/view/setting';
import '@bingads-webui-campaign/ko-stop-binding';
import { mergeStyles } from '@bingads-webui-campaign-react/common-styles';

const getStyles = (theme, palette = {}) => ({
  bidStrategyContainer: {
    ...mergeStyles(
      { theme },
      {
        display: 'flex',
        flexDirection: 'column',

        '& .bid-strategy-container': {
          marginTop: 12,

          '& select': {
            margin: '5px 0',
            width: 460,
          },
          '& div[data-bind="visible: checkCPC"]': {
            paddingTop: 8,
          },
          '& input[type="checkbox"]': {
            float: 'left',
          },
          '& .custom-param__key-field': {
            display: 'inline-block',
            width: 85,
            marginLeft: 5,
            marginRight: 5,
          },
          '& #currencyLabelTargetCpa': {
            display: 'inline-block',
            'vertical-align': 'initial',
            marginTop: 0,
            width: '200px',
          },
          '& #currencyLabelTargetRoas': {
            display: 'inline-block',
            'vertical-align': 'initial',
            marginTop: 0,
            width: '200px',
          },
          '& .alert.alert-info': {
            marginTop: 8,
          },
          '& .alert-image': {
            paddingTop: 3,
          },
          '& a.helpbutton': {
            'margin-left': '5px',
          },
        },
        'bid-strategy-details-container form-group': {
          width: '460px',
        },
        '& .target-cpc-container': {
          whiteSpace: 'nowrap',
        },
        '& .helpbutton': {
          display: 'inline-block',
          marginLeft: 5,
        },

        '& label': {
          display: 'inline-block',
        },

        '& span': {
          verticalAlign: 'middle',
        },

        '& .display-inline-block': {
          display: 'inline-block !important',
        },

        '& .help-block, .remaining-chars-hint-container': {
          textAlign: 'right',
          fontSize: 14,
          margin: 0,
          color: palette.neutralPrimary,
          lineHeight: '24px',
        },

        '& .help-block.error': {
          textAlign: 'left',
        },

        '& ol, ul': {
          listStyle: 'none',
          paddingLeft: 0,
        },

        '& .validation-error': {
          border: '1px solid #A80000 !important',
          backgroundColor: palette.white,
          margin: 0,
        },

        '& .error': {
          color: '#A80000',
        },

        '& div.error': {
          fontSize: 12,
        },

        '& input.error, select.error, textarea.error, input.campaign-error-row, select.campaign-error-row, textarea.campaign-error-row': {
          border: '1px solid #A80000 !important',
        },

        '& .form-group': {
          padding: 0,
          marginBottom: 0,
        },

        '& .form-control': {
          height: 32,
          border: `1px solid ${palette.neutralSecondary}`,
          boxSizing: 'border-box',
          borderRadius: 2,
        },

        '& .form-control:focus': {
          border: `1px solid ${palette.neutralSecondary}`,
        },

        '& .form-control-static': {
          paddingTop: 0,
          paddingBottom: 0,
          margin: 0,
          height: 'auto',
          color: palette.neutralSecondary,

          '&.copy-settings': {
            marginTop: 4,
          },
          '& .max-cpc-input-row': {
            marginLeft: 0,
          },
        },

        '& .control-label, .form-label-container': {
          textAlign: 'left',
          width: '760px',
          marginTop: 33,
          fontSize: 14,
          color: palette.neutralPrimary,
          padding: 0,
          display: 'block',
        },

        '& .form-label-container': {
          '&>label>span': {
            padding: '5px 0px',
            display: 'inline-block',
          },
        },

        '& .control-label .col-sm-4': {
          width: 'auto',
        },

        '& .col-sm-6': {
          paddingLeft: 0,
        },

        '& .displayNone': {
          display: 'none',
        },

        '& select': {
          appearance: 'none',
          '-webkit-appearance': 'none',
        },

        '& textarea': {
          resize: 'none',
        },

        '& .input-group-addon': {
          backgroundColor: palette.white,
          border: 0,
        },
      }
    ),
  },
});

export const LegacyBidStrategy = React.memo(({
  id, className, BiddingScheme, CampaignSettings, campaignType, campaignLanguage, campaignSubType,
  onChange, entityType, hideHeader, popupIds, budgetAmount, validateWithBudget,
  ...restProps
}) => {
  const view = useRef();
  const viewListener = useMemo(() => clone(Backbone.Events), []);

  let container;

  useEffect(() => {
    view.current = new BiddingStrategyView({
      biddingStrategyType: BiddingScheme && BiddingScheme.Type,
      biddingStrategyId: BiddingScheme && BiddingScheme.BidStrategyId,
      targetCpa: BiddingScheme && BiddingScheme.TargetCpa,
      targetRoas: BiddingScheme && BiddingScheme.TargetRoas,
      costPerSale: BiddingScheme && (BiddingScheme.CostPerSale || BiddingScheme.TargetCostPerSale),
      maxCpc: BiddingScheme && BiddingScheme.MaxCpc && BiddingScheme.MaxCpc.Amount,
      targetAdPosition: BiddingScheme && BiddingScheme.TargetAdPosition,
      targetImpressionShare: BiddingScheme && BiddingScheme.TargetImpressionShare,
      entityType,
      campaignSettings: CampaignSettings,
      campaignType,
      campaignLanguage,
      campaignSubType,
      withRealTimeUpdates: true,
      isAfterSave: false,
      hideHeader,
      popupIds,
      budgetAmount,
      validateWithBudget,
      isRenderedInReact: true,
      ...restProps,
    });

    viewListener.listenTo(view.current, 'change', onChange);
    view.current.render();
    view.current.$el.appendTo(container);

    return () => {
      view.current.remove();
    };
  }, [budgetAmount]);

  useEffect(() => {
    viewListener.stopListening(view.current);
    viewListener.listenTo(view.current, 'change', onChange);

    return () => viewListener.stopListening(view.current);
  }, [onChange, viewListener]);

  const classes = useDefaultStyles(getStyles);

  return (
    <div
      id={id}
      className={classnames(className, classes.bidStrategyContainer)}
    >
      <div ref={(ref) => { container = ref; }} />
    </div>
  );
});

LegacyBidStrategy.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  BiddingScheme: PropTypes.shape({
    BidStrategyId: PropTypes.number,
    Type: PropTypes.string,
    TargetCpa: PropTypes.string,
    TargetRoas: PropTypes.string,
    TargetCostPerSale: PropTypes.string,
    CostPerSale: PropTypes.string,
    MaxCpc: PropTypes.shape({
      Amount: PropTypes.number,
    }),
    TargetAdPosition: PropTypes.string,
    TargetImpressionShare: PropTypes.number,
  }),
  CampaignSettings: PropTypes.arrayOf(PropTypes.shape({})),
  entityType: PropTypes.string,
  campaignType: PropTypes.string,
  campaignLanguage: PropTypes.string,
  campaignSubType: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool,
  popupIds: PropTypes.shape({
    BidStrategy: PropTypes.string,
  }),
  budgetAmount: PropTypes.number,
  validateWithBudget: PropTypes.bool,
};

LegacyBidStrategy.defaultProps = {
  id: undefined,
  className: undefined,
  CampaignSettings: [],
  budgetAmount: undefined,
  validateWithBudget: false,
  BiddingScheme: undefined,
  campaignType: undefined,
  campaignLanguage: 'English',
  campaignSubType: undefined,
  entityType: undefined,
  hideHeader: false,
  popupIds: {
    BidStrategy: 'pop_BA_CampaignGrids_BidStrategy',
  },
};
